import React from 'react';
import cn from 'classnames';
import A_TextArea from '@/components/A_Atoms/A_TextArea/A_TextArea';
import A_CircleButton from '@/components/A_Atoms/A_CircleButton/A_CircleButton';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import { useTranslation } from '@/hooks/useTranslation';
import { getMediaUrl } from '@/utils/mediaUrl';
import {
  resolveCommentStoneImage,
  CommentStoneOption,
} from '@/utils/commentStones';
import ReactionFallback from '@/assets/images/A_UserAvatar.png';
import TunnelStoneSelector from './TunnelStoneSelector';
import { CapsuleReaction } from './types';
import styles from './CapsuleReactionsView.module.css';

interface TunnelCenterPanelProps {
  tunnelCenterView: 'stones' | 'comment';
  selectedComment: CapsuleReaction | null;
  tunnelStoneOptions: CommentStoneOption[];
  selectedStoneId: string;
  onStoneSelect: (stone: CommentStoneOption) => void | Promise<void>;
  onPreviewDragStart: () => void;
  onPreviewDragEnd: () => void;
  isStoneSelectorDisabled: boolean;
  balance: number | null;
  isAuthenticated: boolean;
  commentDraft: string;
  onCommentDraftChange: (value: string) => void;
  onCommentSubmit: () => void;
  onBackToStones: () => void;
}

export default function TunnelCenterPanel({
  tunnelCenterView,
  selectedComment,
  tunnelStoneOptions,
  selectedStoneId,
  onStoneSelect,
  onPreviewDragStart,
  onPreviewDragEnd,
  isStoneSelectorDisabled,
  balance,
  isAuthenticated,
  commentDraft,
  onCommentDraftChange,
  onCommentSubmit,
  onBackToStones,
}: TunnelCenterPanelProps) {
  const t = useTranslation();

  if (tunnelCenterView === 'comment' && selectedComment?.text) {
    return (
      <div className={styles.tunnel_comment_full}>
        <button
          type='button'
          className={styles.tunnel_comment_full_back}
          onClick={onBackToStones}
          aria-label={t('capsuleReactions.back')}
        >
          <Q_Icon.ArrowLeftIcon
            width={8}
            height={16}
            fill='none'
            stroke='currentColor'
          />
          <span
            className={cn(styles.tunnel_comment_full_back_text, 'text-caption')}
          >
            {t('capsuleReactions.back')}
          </span>
        </button>
        <div className={styles.tunnel_comment_full_header}>
          <img
            src={
              resolveCommentStoneImage(
                selectedComment.commentObject,
                tunnelStoneOptions
              ) ||
              (selectedComment.author?.avatar
                ? getMediaUrl(selectedComment.author.avatar)
                : ReactionFallback)
            }
            alt=''
            className={styles.tunnel_comment_full_avatar}
          />
          <div className={styles.tunnel_comment_full_meta}>
            <span className={cn(styles.tunnel_comment_full_author, 'text-p-6')}>
              {selectedComment.author?.name ?? ''}
            </span>
          </div>
        </div>
        <p className={cn(styles.tunnel_comment_full_text, 'text-p-7')}>
          {selectedComment.text}
        </p>
      </div>
    );
  }

  return (
    <>
      <TunnelStoneSelector
        stoneOptions={tunnelStoneOptions}
        selectedStoneId={selectedStoneId}
        onSelect={onStoneSelect}
        onDragStart={onPreviewDragStart}
        onDragEnd={onPreviewDragEnd}
        disabled={isStoneSelectorDisabled}
        balance={balance}
      />
      {isAuthenticated && (
        <div className={styles.tunnel_comment_form}>
          <A_TextArea
            label={t('capsuleReactions.commentLabel')}
            value={commentDraft}
            onChange={onCommentDraftChange}
            background='gray'
            height={160}
            maxLength={900}
            maxWidth='100%'
          />
          <A_CircleButton
            icon={<Q_Icon.SendIcon width={24} height={24} fill='none' />}
            size='large'
            variant='secondary'
            onClick={onCommentSubmit}
          />
        </div>
      )}
    </>
  );
}
